import { useEffect, useRef, useState } from 'react';
import styles from './Dropdown.module.css';

export interface DropdownOption {
  value: string;
  label: string;
}

// Reine Multi-Toggle-Logik, separat exportiert damit sie ohne DOM-Rendering (kein
// jsdom/testing-library in diesem Repo) unit-testbar ist: entfernt den Wert, wenn er
// schon selektiert ist, haengt ihn sonst an (Reihenfolge = Klick-Reihenfolge).
export function toggleValue(current: string[], value: string): string[] {
  return current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
}

// Typeahead: erster Options-Index, dessen Label mit dem getippten Puffer beginnt
// (case-insensitiv). Leerer Puffer -> -1 (kein Treffer). Pure, separat testbar.
export function findTypeaheadIndex(options: readonly DropdownOption[], buffer: string): number {
  if (!buffer) return -1;
  const b = buffer.toLowerCase();
  return options.findIndex((o) => o.label.toLowerCase().startsWith(b));
}

interface BaseProps {
  options: readonly DropdownOption[];
  disabled?: boolean;
  placeholder?: string;
}
interface SingleProps extends BaseProps {
  multiple?: false;
  value: string;
  onChange: (next: string) => void;
}
interface MultiProps extends BaseProps {
  multiple: true;
  value: string[];
  onChange: (next: string[]) => void;
  // Werte, die nicht abgewählt werden dürfen (z.B. durch Sprachprobe belegt) —
  // Checkbox disabled + Schloss-Marker; Entfernen wird zusätzlich hart geblockt.
  lockedValues?: readonly string[];
  // Tooltip an gesperrten Werten (Standard deutsch; die Self-Service-Fläche übersetzt).
  lockedTitle?: string;
}
type Props = SingleProps | MultiProps;

// Wiederverwendbares Dropdown fuer single- UND multi-select — ein einziges Styling
// (Trigger-Box + Chevron + Panel) fuer alle Selects im Tool. `multiple` schaltet das
// Verhalten: single schliesst nach der Auswahl und traegt genau einen Wert; multi
// bleibt offen und toggelt Werte (Checkbox-Optionen).
export default function Dropdown(props: Props) {
  const { options, disabled = false, placeholder = '—' } = props;
  const isMulti = props.multiple === true;
  const [open, setOpen] = useState(false);
  // Panel klappt nach oben statt unten, wenn unten zu wenig Platz ist (z.B. letztes
  // Filter-Dropdown unten in der scrollbaren Sidebar) → kein Clipping/Scrollen nötig.
  const [dropUp, setDropUp] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Tastatur-Navigation im offenen Panel: getippte Buchstaben springen zur ersten
  // passenden Option; Pfeiltasten bewegen die Hervorhebung; Enter setzt sie.
  // activeIndex hebt die aktuelle Option optisch hervor (wie Hover).
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const optionRefs = useRef<(HTMLElement | null)[]>([]);
  const typeahead = useRef<{ buffer: string; timer: ReturnType<typeof setTimeout> | undefined }>({ buffer: '', timer: undefined });

  // Scrollt nur INNERHALB des Panels (nie die Seite): 'top' setzt die Option ans
  // obere Ende (Browser klemmt am Listenende), 'nearest' scrollt minimal, damit
  // die Option bei Pfeil-Navigation gerade sichtbar bleibt.
  function scrollOptionIntoPanel(idx: number, align: 'top' | 'nearest') {
    const panel = panelRef.current;
    const el = optionRefs.current[idx];
    if (!panel || !el) return;
    const elTop = el.offsetTop;
    if (align === 'top') { panel.scrollTop = elTop; return; }
    const elBot = elTop + el.offsetHeight;
    if (elTop < panel.scrollTop) panel.scrollTop = elTop;
    else if (elBot > panel.scrollTop + panel.clientHeight) panel.scrollTop = elBot - panel.clientHeight;
  }

  function toggleOpen() {
    if (!open && rootRef.current) {
      const rect = rootRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // Panel-Maxhoehe ~220px (+ Rand); nach oben, wenn unten zu wenig UND oben mehr Platz.
      setDropUp(spaceBelow < 240 && rect.top > spaceBelow);
    }
    setOpen((o) => !o);
  }

  useEffect(() => {
    if (!open) return;
    function onDocPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocPointerDown);
    return () => document.removeEventListener('mousedown', onDocPointerDown);
  }, [open]);
  // Beim Wechsel in den disabled-Zustand (Ansicht/Bearbeiten-Toggle) ein offenes
  // Panel schliessen.
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  // Panel-Schliessen räumt Typeahead-Puffer + Hervorhebung auf.
  useEffect(() => {
    if (open) return;
    setActiveIndex(null);
    clearTimeout(typeahead.current.timer);
    typeahead.current.buffer = '';
  }, [open]);
  // Timer beim Unmount aufräumen.
  useEffect(() => () => clearTimeout(typeahead.current.timer), []);

  // Tastatur-Navigation im offenen Panel:
  //  - Pfeil ab/auf: Hervorhebung bewegen (scrollt minimal mit)
  //  - Enter: hervorgehobene Option setzen (choose = single schließt, multi toggelt)
  //  - druckbare Zeichen: Typeahead-Puffer (600ms Reset) → erste passende Option
  //    ans obere Panel-Ende scrollen. Führendes Leerzeichen ignorieren, damit die
  //    Space-Taste nicht als Suchbeginn zählt (und der Button nicht toggelt).
  function onKeyDown(e: React.KeyboardEvent) {
    if (!open) return;
    if (options.length === 0) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const last = options.length - 1;
      let next;
      if (e.key === 'ArrowDown') next = activeIndex == null ? 0 : Math.min(last, activeIndex + 1);
      else next = activeIndex == null ? last : Math.max(0, activeIndex - 1);
      setActiveIndex(next);
      scrollOptionIntoPanel(next, 'nearest');
      return;
    }
    if (e.key === 'Enter') {
      if (activeIndex != null && options[activeIndex]) { e.preventDefault(); choose(options[activeIndex].value); }
      return;
    }
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === ' ' && !typeahead.current.buffer) return;
    e.preventDefault();
    const ta = typeahead.current;
    clearTimeout(ta.timer);
    ta.buffer += e.key;
    ta.timer = setTimeout(() => { ta.buffer = ''; }, 600);
    const idx = findTypeaheadIndex(options, ta.buffer);
    if (idx >= 0) {
      setActiveIndex(idx);
      scrollOptionIntoPanel(idx, 'top');
    }
  }

  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  const isSelected = (v: string) => (props.multiple ? props.value.includes(v) : props.value === v);
  // hasValue steuert nur die Platzhalter-Optik (ausgegraut). Bei single wird der
  // aktuelle Wert immer als Options-Label angezeigt — auch der Leerwert '' (z.B.
  // „Ohne Ranking"), nur dann in Platzhalter-Optik.
  const hasValue = isMulti ? props.value.length > 0 : props.value !== '';
  const summary = isMulti
    ? (props.value.length ? props.value.map(labelFor).join(', ') : placeholder)
    : (options.find((o) => o.value === props.value)?.label ?? placeholder);

  const lockedSet = props.multiple && props.lockedValues ? new Set(props.lockedValues) : null;
  const lockedTitle = (props.multiple && props.lockedTitle) || 'durch Sprachprobe belegt';

  function choose(v: string) {
    if (props.multiple) {
      // Gesperrten, bereits selektierten Wert nicht entfernen (belegt durch Probe).
      if (props.lockedValues?.includes(v) && props.value.includes(v)) return;
      props.onChange(toggleValue(props.value, v));
    } else {
      props.onChange(v);
      setOpen(false);
    }
  }

  return (
    <div className={styles.wrap} ref={rootRef} onKeyDown={onKeyDown}>
      <button
        type="button"
        className={styles.trigger}
        disabled={disabled}
        onClick={toggleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={hasValue ? styles.summary : styles.summaryPlaceholder}>{summary}</span>
        <svg className={open ? styles.chevronOpen : styles.chevron} width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      </button>
      {open && !disabled && (
        <div ref={panelRef} className={dropUp ? styles.panelUp : styles.panel} role="listbox" aria-multiselectable={isMulti}>
          {options.map((opt, i) => {
            const isSel = isSelected(opt.value);
            const locked = !!lockedSet?.has(opt.value) && isSel;
            const active = activeIndex === i;
            return isMulti ? (
              <label
                key={opt.value}
                ref={(el) => { optionRefs.current[i] = el; }}
                role="option"
                aria-selected={isSel}
                className={active ? `${styles.option} ${styles.optionActive}` : styles.option}
                title={locked ? lockedTitle : undefined}
              >
                <input type="checkbox" checked={isSel} disabled={locked} onChange={() => choose(opt.value)} />
                {opt.label}
                {locked && <span className={styles.check} aria-hidden="true">🔒</span>}
              </label>
            ) : (
              <button
                key={opt.value}
                ref={(el) => { optionRefs.current[i] = el; }}
                type="button"
                role="option"
                aria-selected={isSel}
                className={`${isSel ? styles.optionButtonSelected : styles.optionButton}${active ? ` ${styles.optionActive}` : ''}`}
                onClick={() => choose(opt.value)}
              >
                <span>{opt.label}</span>
                {isSel && <span className={styles.check} aria-hidden="true">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
